import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type { ActionInput, MeetingAction } from '../../actions/types'
import type { MinuteItem, MinuteItemInput, MinuteItemKind } from '../../minuteItems/types'
import type {
  MeetingContentSection,
  MeetingDetail,
  MeetingGroup,
  MeetingGroupQuery,
  MeetingGroupUpsert,
  MeetingListItem,
  MeetingLogEntry,
  MeetingUpdateRequest,
  PagedResponse,
} from '../types'

const BASE = '/management/meeting-groups'
const MEETINGS = '/management/meetings'

/** İçerik bölümü tanımları (aktif karar tipleri, SORT_INDEX ASC) — içerik listeleri başlık/sırası dinamik. */
export async function getMeetingContentSections(): Promise<MeetingContentSection[]> {
  const { data } = await apiClient.get<MeetingContentSection[]>('/management/meeting-content-sections')
  return data
}

/** Tekil grup (breadcrumb/başlık — 044). */
export async function getMeetingGroup(id: number): Promise<MeetingGroup> {
  const { data } = await apiClient.get<MeetingGroup>(`${BASE}/${id}`)
  return data
}

/** Tekil toplantı (tutanak) detayı — temel bilgiler + katılımcılar + dökümanlar (044). */
export async function getMeetingDetail(id: number): Promise<MeetingDetail> {
  const { data } = await apiClient.get<MeetingDetail>(`${MEETINGS}/${id}`)
  return data
}

/** Toplantı düzenleme (044): ad/tarih/saat/yer + katılımcılar. Güncel detay döner. Bayat modDate → 409. */
export async function updateMeeting(id: number, body: MeetingUpdateRequest): Promise<MeetingDetail> {
  const { data } = await apiClient.put<MeetingDetail>(`${MEETINGS}/${id}`, body)
  return data
}

/** Taslak tutanağı Yayınla (360→361) + standart PDF üret/iliştir. Güncel detay döner. Arşivli/yayınlı → 409. */
export async function publishMeeting(id: number): Promise<MeetingDetail> {
  const { data } = await apiClient.post<MeetingDetail>(`${MEETINGS}/${id}/publish`)
  return data
}

/**
 * Aksiyonları bağımsız senkronlar (detay ekranı — hep düzenlenebilir, anında kaydet). Tam liste (upsert +
 * gövdede olmayanı soft-delete). Güncel aksiyon listesi (numara/durum adı çözülmüş) döner.
 */
export async function syncMeetingActions(
  meetingId: number,
  actions: ActionInput[],
): Promise<MeetingAction[]> {
  const { data } = await apiClient.put<MeetingAction[]>(`${MEETINGS}/${meetingId}/actions`, { actions })
  return data
}

/** Kind → sync uç segmenti (backend: /agenda, /principles, /decisions). */
const ITEM_PATH: Record<MinuteItemKind, string> = {
  agenda: 'agenda',
  principles: 'principles',
  decisions: 'decisions',
}

/**
 * Basit içerik maddelerini (Gündem/Prensip/Karar) bağımsız senkronlar (detay ekranı — hep düzenlenebilir,
 * anında kaydet). Tam liste (upsert + gövdede olmayanı soft-delete). Güncel liste (numara çözülmüş) döner.
 */
export async function syncMeetingItems(
  meetingId: number,
  kind: MinuteItemKind,
  items: MinuteItemInput[],
): Promise<MinuteItem[]> {
  const { data } = await apiClient.put<MinuteItem[]>(
    `${MEETINGS}/${meetingId}/${ITEM_PATH[kind]}`,
    { items },
  )
  return data
}

/**
 * Gündem maddelerini yeniden sıralar (sürükle-bırak): {@code orderedIds} istenen sırada; backend record_number'ı
 * 1..N yazar → görünen "No" da değişir. Güncel (yeniden numaralı, sıralı) gündem listesi döner.
 */
export async function reorderMeetingAgenda(
  meetingId: number,
  orderedIds: number[],
): Promise<MinuteItem[]> {
  const { data } = await apiClient.put<MinuteItem[]>(
    `${MEETINGS}/${meetingId}/agenda/reorder`,
    { orderedIds },
  )
  return data
}

/**
 * **Tek maddeyi siler** — gündem · prensip kararı · karar (2026-09-11). Soft-delete + denetim izi.
 *
 * <p>Senkron ucundan ({@link syncMeetingItems}) ayrıdır ve `RECORD_DELETE` yetkisiyle korunur: senkron
 * `MINUTES_CREATE` ister, yani silme hakkı verilmek istenen role tutanağın tamamını düzenleme yetkisi
 * vermek gerekirdi. Düzenleme yetkisi olan kullanıcı maddeyi yine senkronla kaldırabilir; bu uç, yalnız
 * silme yetkisi olanlar (ör. Genel Müdür) içindir.
 */
export async function deleteMeetingItem(meetingId: number, itemId: number): Promise<void> {
  await apiClient.delete(`${MEETINGS}/${meetingId}/items/${itemId}`)
}

/** Tutanaktan bir dosya ekini kaldırır (soft-delete). */
export async function deleteMeetingAttachment(meetingId: number, attachmentId: number): Promise<void> {
  await apiClient.delete(`/management/minutes/${meetingId}/attachments/${attachmentId}`)
}

/** İşlem Geçmişi: toplantının denetim kayıtları (en yeni önce; op adı + kullanıcı + çözülmüş değişiklik). */
export async function getMeetingLogs(meetingId: number): Promise<MeetingLogEntry[]> {
  const { data } = await apiClient.get<MeetingLogEntry[]>(`/management/minutes/${meetingId}/logs`)
  return data
}

/** Sayfalı grup listesi (ada göre arama) + her grubun aktif toplantı sayısı. */
export async function getMeetingGroups(query: MeetingGroupQuery): Promise<PagedResponse<MeetingGroup>> {
  const params: Record<string, string | number | boolean> = {
    page: query.page ?? 0,
    size: query.size ?? 12,
  }
  if (query.q && query.q.trim()) params.q = query.q.trim()
  if (query.withMeetings) params.withMeetings = true
  const { data } = await apiClient.get<PagedResponse<MeetingGroup>>(BASE, { params })
  return data
}

export async function createMeetingGroup(body: MeetingGroupUpsert): Promise<MeetingGroup> {
  const { data } = await apiClient.post<MeetingGroup>(BASE, body)
  return data
}

export async function updateMeetingGroup(id: number, body: MeetingGroupUpsert): Promise<MeetingGroup> {
  const { data } = await apiClient.put<MeetingGroup>(`${BASE}/${id}`, body)
  return data
}

export async function deleteMeetingGroup(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`)
}

/** Arşiv ekranı (salt-okuma): tüm gruplardaki arşivli (362) toplantılar, sayfalı. */
export async function getArchivedMeetings(
  query: { page?: number; size?: number; sort?: string },
): Promise<PagedResponse<MeetingListItem>> {
  const params: Record<string, string | number> = { page: query.page ?? 0, size: query.size ?? 20 }
  if (query.sort) params.sort = query.sort
  const { data } = await apiClient.get<PagedResponse<MeetingListItem>>(`${MEETINGS}/archived`, {
    params,
  })
  return data
}

/**
 * "Taslaklar" kartı: tüm gruplardaki taslak (360) toplantılar, sayfalı. Taslaklar grup kartlarında/
 * drill-down'ında görünmez; buradan detaya girilip tamamlanır ve yayınlanır.
 */
export async function getDraftMeetings(
  query: { page?: number; size?: number },
): Promise<PagedResponse<MeetingListItem>> {
  const params = { page: query.page ?? 0, size: query.size ?? 20 }
  const { data } = await apiClient.get<PagedResponse<MeetingListItem>>(`${MEETINGS}/drafts`, {
    params,
  })
  return data
}

/** Drill-down: bir grubun aktif toplantıları (salt-okuma). */
export async function getGroupMeetings(
  groupId: number,
  query: { page?: number; size?: number; sort?: string },
): Promise<PagedResponse<MeetingListItem>> {
  const params: Record<string, string | number> = { page: query.page ?? 0, size: query.size ?? 50 }
  if (query.sort) params.sort = query.sort
  const { data } = await apiClient.get<PagedResponse<MeetingListItem>>(
    `${BASE}/${groupId}/meetings`,
    { params },
  )
  return data
}

/**
 * Arşivlenmiş toplantıları Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportArchivedMeetings(sort?: string): Promise<void> {
  await downloadXlsx(`${MEETINGS}/archived/export`, { sort }, 'arsiv-toplantilari.xlsx')
}

/**
 * Taslak toplantıları Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportDraftMeetings(): Promise<void> {
  await downloadXlsx(`${MEETINGS}/drafts/export`, {}, 'taslak-toplantilar.xlsx')
}

/**
 * Toplantı gruplarını Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportMeetingGroups(q?: string): Promise<void> {
  await downloadXlsx(`${BASE}/export`, { q }, 'toplanti-gruplari.xlsx')
}

/**
 * Bir grubun toplantılarını Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportGroupMeetings(groupId: number, sort?: string): Promise<void> {
  await downloadXlsx(`${BASE}/${groupId}/meetings/export`, { sort }, 'grup-toplantilari.xlsx')
}
